import DeleteConfirmPopover from "components/common/DeleteConfirmPopover";
import {
  Badge,
  Box,
  Card,
  Flex,
  Heading,
  HStack,
  notify,
  RefreshButton,
  Text,
  VStack,
} from "components/ui";
import { useTranslation } from "hooks/useTranslation";
import { useCallback, useEffect, useState } from "react";
import { MdKey, MdPerson } from "react-icons/md";
import {
  AutonomousApiToken,
  autonomousTokenService,
} from "services/autonomousTokenService";
import { getThemeColor } from "theme/colors";

/**
 * Per-user access tokens. A company admin generates tokens through the token endpoint
 * (POST /api/autonomous/token, admin-authenticated, verifying the end-user's credential) —
 * this panel does not create them; it lists the company's active tokens and lets an admin
 * revoke any of them. Only the PBKDF2 hash is ever stored server-side; the plaintext is
 * shown once, at generation time.
 */
const AccessTokensPanel = () => {
  const { t } = useTranslation();
  const [tokens, setTokens] = useState<AutonomousApiToken[]>([]);
  const [loading, setLoading] = useState(true);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setTokens(await autonomousTokenService.getAll());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleRevoke = async (id: string) => {
    setRevokingId(id);
    try {
      await autonomousTokenService.revoke(id);
      notify({
        type: "success",
        title: t("app.autonomousAgents.notifications.tokenRevoked"),
      });
      await load();
    } finally {
      setRevokingId(null);
    }
  };

  const fmt = (iso: string | null) =>
    iso
      ? new Date(iso).toLocaleString()
      : t("app.autonomousAgents.accessTokens.never");

  return (
    <Card.Root>
      <Card.Body>
        <VStack align="stretch" gap={4}>
          <Flex justify="space-between" align="center" wrap="wrap" gap={3}>
            <Box>
              <Heading size="md">
                {t("app.autonomousAgents.accessTokens.title")}
              </Heading>
              <Text fontSize="sm" color={getThemeColor("text.secondary")}>
                {t("app.autonomousAgents.accessTokens.subtitle")}
              </Text>
            </Box>
            <RefreshButton onClick={load} loading={loading} iconOnly />
          </Flex>

          {/* Token list */}
          {tokens.length === 0 ? (
            <Flex align="center" justify="center" py={8}>
              <VStack gap={2}>
                <MdKey size={28} color="gray" />
                <Text fontSize="sm" color={getThemeColor("text.secondary")}>
                  {t("app.autonomousAgents.accessTokens.empty")}
                </Text>
              </VStack>
            </Flex>
          ) : (
            <VStack align="stretch" gap={0}>
              {tokens.map((token) => (
                <Flex
                  key={token.id}
                  justify="space-between"
                  align="center"
                  py={3}
                  borderBottom="1px solid"
                  borderColor={getThemeColor("border.default")}
                  gap={3}
                >
                  <VStack align="start" gap={1}>
                    <HStack gap={2} wrap="wrap">
                      <Text fontSize="sm" fontWeight="semibold">
                        {token.name ||
                          t("app.autonomousAgents.accessTokens.unnamed")}
                      </Text>
                      {token.customerExternalId && (
                        <Badge
                          colorPalette="blue"
                          title={t(
                            "app.autonomousAgents.accessTokens.perUserHint",
                          )}
                        >
                          <MdPerson />
                          {token.customerExternalId}
                        </Badge>
                      )}
                    </HStack>
                    <HStack gap={3} wrap="wrap">
                      <Text
                        fontSize="xs"
                        color={getThemeColor("text.secondary")}
                      >
                        {t("app.autonomousAgents.accessTokens.created")}:{" "}
                        {fmt(token.createdAt)}
                      </Text>
                      <Text
                        fontSize="xs"
                        color={getThemeColor("text.secondary")}
                      >
                        {t("app.autonomousAgents.accessTokens.lastUsed")}:{" "}
                        {fmt(token.lastUsedAt)}
                      </Text>
                    </HStack>
                  </VStack>
                  <DeleteConfirmPopover
                    onConfirm={() => handleRevoke(token.id)}
                    loading={revokingId === token.id}
                    title={t(
                      "app.autonomousAgents.accessTokens.revokeConfirmTitle",
                    )}
                    message={t(
                      "app.autonomousAgents.accessTokens.revokeConfirm",
                    )}
                    triggerLabel={t("app.autonomousAgents.accessTokens.revoke")}
                    confirmLabel={t("app.autonomousAgents.accessTokens.revoke")}
                  />
                </Flex>
              ))}
            </VStack>
          )}
        </VStack>
      </Card.Body>
    </Card.Root>
  );
};

export default AccessTokensPanel;
